import React, { useState, useMemo, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { monthYearLabel, fullDateLabel, monthlyTicks } from "../../lib/indicators";

const RANGE_OPTIONS = [
  { value: "ALL", label: "Todo histórico", days: 1500 },
  { value: "12M", label: "Últimos 12 meses", days: 252 },
  { value: "6M", label: "Últimos 6 meses", days: 126 },
];

const FIELD_OPTIONS = [
  { value: "open", label: "Abertura" },
  { value: "high", label: "Máxima" },
  { value: "low", label: "Mínima" },
  { value: "close", label: "Fechamento" },
];

const M1_MIN = 2;
const M1_MAX = 550;
const M2_MIN = 4;
const M2_MAX = 730;

const GRAY = "oklch(0.552 0.016 285.938)";
const GREEN = "oklch(0.48 0.16 145)";
const M2_COLOR = "oklch(0.55 0.21 25)";     // vermelho para toda a Média 2 (solid quando >= M1)
const M2_CROSS_COLOR = "oklch(0.45 0.23 25)"; // vermelho mais intenso quando < M1 (tracejado)

function trailingMean(values, window) {
  const out = new Array(values.length).fill(null);
  let sum = 0;
  let count = 0;
  for (let i = 0; i < values.length; i++) {
    const v = Number(values[i]);
    sum += v;
    count += 1;
    if (i >= window) {
      sum -= Number(values[i - window]);
      count -= 1;
    }
    if (count >= window) {
      out[i] = sum / window;
    }
  }
  return out;
}

function clamp(value, min, max) {
  const n = Number(value);
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.min(max, Math.round(n)));
}

import { apiClient } from "../../lib/api";
import { toast } from "sonner";
import { Save, Trash2, TrendingUp, TrendingDown } from "lucide-react";

export default function CustomTrendChart({
  history,
  currency = "USD",
  stockId = null,
  range,
  setRange,
  field,
  setField,
  m1Days,
  setM1Days,
  m2Days,
  setM2Days,
}) {
  const [savedList, setSavedList] = useState([]);
  const [savingConfig, setSavingConfig] = useState(false);
  const [loadingSaved, setLoadingSaved] = useState(false);

  const refreshSaved = React.useCallback(async () => {
    if (!stockId) return;
    setLoadingSaved(true);
    try {
      const r = await apiClient.get(`/stocks/${stockId}/saved-ma`);
      setSavedList(r.data || []);
    } catch {
      /* silent */
    } finally {
      setLoadingSaved(false);
    }
  }, [stockId]);

  useEffect(() => {
    refreshSaved();
  }, [refreshSaved]);

  const m1DaysClamped = clamp(m1Days, M1_MIN, M1_MAX);
  const m2DaysRaw = clamp(m2Days, M2_MIN, M2_MAX);
  // Rule: Média 2 must always be strictly greater than Média 1.
  const m2DaysClamped = Math.min(M2_MAX, Math.max(m2DaysRaw, m1DaysClamped + 1));

  // Whenever Média 1 rises above (or equals) the current input value of Média 2,
  // nudge the input so it stays visibly greater than M1.
  useEffect(() => {
    const current = Number(m2Days);
    if (Number.isFinite(current) && current <= m1DaysClamped) {
      setM2Days(Math.min(M2_MAX, m1DaysClamped + 1));
    }
  }, [m1DaysClamped]); // eslint-disable-line react-hooks/exhaustive-deps

  const { rows, ticks, yDomain } = useMemo(() => {
    if (!history || history.length === 0) {
      return { rows: [], ticks: [], yDomain: ["auto", "auto"] };
    }
    const fullValues = history.map((d) => Number(d[field]));
    const m1 = trailingMean(fullValues, m1DaysClamped);
    const m2Full = trailingMean(fullValues, m2DaysClamped);

    const m2_blue = new Array(history.length).fill(null);
    const m2_red = new Array(history.length).fill(null);
    let prevState = null;
    for (let i = 0; i < history.length; i++) {
      const v2 = m2Full[i];
      const v1 = m1[i];
      if (v2 == null || v1 == null) {
        prevState = null;
        continue;
      }
      const state = v2 >= v1 ? "blue" : "red";
      if (state === "blue") m2_blue[i] = v2;
      else m2_red[i] = v2;
      if (prevState && prevState !== state && i > 0) {
        if (prevState === "blue") m2_blue[i] = v2;
        else m2_red[i] = v2;
      }
      prevState = state;
    }

    const fullRows = history.map((d, i) => ({
      raw_date: d.quote_date,
      price: +Number(d[field]).toFixed(4),
      m1: m1[i] != null ? +m1[i].toFixed(4) : null,
      m2_blue: m2_blue[i] != null ? +m2_blue[i].toFixed(4) : null,
      m2_red: m2_red[i] != null ? +m2_red[i].toFixed(4) : null,
    }));

    const rangeDays = RANGE_OPTIONS.find((o) => o.value === range).days;
    const start = Math.max(0, fullRows.length - rangeDays);
    const sliced = fullRows.slice(start);

    // Dynamic Y-axis: absolute min/max of the visible values across price +
    // Media 1 + Media 2, with 5% padding on each side.
    let vmin = Infinity;
    let vmax = -Infinity;
    for (const r of sliced) {
      for (const v of [r.price, r.m1, r.m2_blue, r.m2_red]) {
        if (v == null || !Number.isFinite(v)) continue;
        if (v < vmin) vmin = v;
        if (v > vmax) vmax = v;
      }
    }
    let dom;
    if (!Number.isFinite(vmin) || !Number.isFinite(vmax) || vmin === vmax) {
      dom = ["auto", "auto"];
    } else {
      const lo = Math.max(0, vmin * 0.95);
      const hi = vmax * 1.05;
      dom = [+lo.toFixed(2), +hi.toFixed(2)];
    }

    return { rows: sliced, ticks: monthlyTicks(sliced), yDomain: dom };
  }, [history, range, field, m1DaysClamped, m2DaysClamped]);

  const fieldLabel = FIELD_OPTIONS.find((o) => o.value === field).label;

  // Interpretive summary: check the last valid point where both M1 and M2 exist
  const interpretation = useMemo(() => {
    if (!rows || rows.length === 0) return null;
    let last = null;
    for (let i = rows.length - 1; i >= 0; i--) {
      const r = rows[i];
      if (r.m1 != null && (r.m2_blue != null || r.m2_red != null)) {
        last = { ...r, m2: r.m2_blue ?? r.m2_red };
        break;
      }
    }
    if (!last) {
      return {
        state: "insufficient",
        title: "Histórico insuficiente para leitura",
        text: "Amplie o período ou reduza as janelas das médias para gerar uma leitura.",
      };
    }
    const priceAboveM1 = last.price >= last.m1;
    const priceAboveM2 = last.price >= last.m2;
    const m2AboveM1 = last.m2 >= last.m1;
    if (m2AboveM1 && priceAboveM1 && priceAboveM2) {
      return {
        state: "bullish",
        title: "Cenário de alta (bullish)",
        text: `O preço atual (${fieldLabel.toLowerCase()}) está acima das duas médias, e a Média 2 (${m2DaysClamped}d) segue acima da Média 1 (${m1DaysClamped}d). É um alinhamento típico de tendência de alta consolidada. Costuma sinalizar continuidade da força compradora, mas atenção para eventual sobrecompra.`,
      };
    }
    if (!m2AboveM1 && !priceAboveM1 && !priceAboveM2) {
      return {
        state: "bearish",
        title: "Cenário de baixa (bearish)",
        text: `A Média 2 (${m2DaysClamped}d) está abaixo da Média 1 (${m1DaysClamped}d) e o preço abaixo das duas médias — configuração de tendência de baixa em curso. Costuma indicar pressão vendedora persistente até uma reversão clara.`,
      };
    }
    if (m2AboveM1 && !priceAboveM1) {
      return {
        state: "correction",
        title: "Correção dentro de uma alta",
        text: `A tendência de longo prazo (Média 2 > Média 1) segue positiva, mas o preço rompeu abaixo das médias — pode ser realização de lucros ou início de correção. Vale monitorar se as médias começam a virar.`,
      };
    }
    if (!m2AboveM1 && priceAboveM1) {
      return {
        state: "reversal",
        title: "Possível reversão em construção",
        text: `As médias ainda indicam baixa (Média 2 < Média 1), mas o preço recuperou e voltou acima delas. É um sinal inicial de reversão que precisa ser confirmado pela virada das próprias médias.`,
      };
    }
    return {
      state: "neutral",
      title: "Cenário misto / lateral",
      text: "Preço e médias trocando de posição sem alinhamento claro — leitura pouco conclusiva. Aguarde uma direção mais consistente antes de dimensionar posição.",
    };
  }, [rows, fieldLabel, m1DaysClamped, m2DaysClamped]);

  const interpretationTone = {
    bullish: "border-buy bg-buy-soft text-buy-strong",
    bearish: "border-sell bg-sell-soft text-sell-strong",
    correction: "border-amber-400 bg-amber-50 text-amber-900",
    reversal: "border-amber-400 bg-amber-50 text-amber-900",
    neutral: "border-border bg-muted/40 text-foreground",
    insufficient: "border-border bg-muted/40 text-muted-foreground",
  };

  return (
    <div className="space-y-4" data-testid="custom-trend-chart">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Período</Label>
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger data-testid="trend-range-select" className="text-sm filter-yellow">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Momento (linha cinza)</Label>
          <Select value={field} onValueChange={setField}>
            <SelectTrigger data-testid="trend-field-select" className="text-sm filter-yellow">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FIELD_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label
            htmlFor="m1-days"
            className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground"
          >
            Média 1 (dias · {M1_MIN}-{M1_MAX})
          </Label>
          <Input
            id="m1-days"
            type="number"
            inputMode="numeric"
            min={M1_MIN}
            max={M1_MAX}
            step={1}
            value={m1Days}
            onChange={(e) => setM1Days(e.target.value)}
            onBlur={(e) => setM1Days(clamp(e.target.value, M1_MIN, M1_MAX))}
            className="font-mono-tab text-sm filter-yellow"
            data-testid="trend-m1-input"
          />
        </div>
        <div className="space-y-1">
          <Label
            htmlFor="m2-days"
            className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground"
          >
            Média 2 (dias · {M2_MIN}-{M2_MAX}, sempre &gt; Média 1)
          </Label>
          <Input
            id="m2-days"
            type="number"
            inputMode="numeric"
            min={M2_MIN}
            max={M2_MAX}
            step={1}
            value={m2Days}
            onChange={(e) => setM2Days(e.target.value)}
            onBlur={(e) =>
              setM2Days(
                Math.min(
                  M2_MAX,
                  Math.max(clamp(e.target.value, M2_MIN, M2_MAX), m1DaysClamped + 1),
                ),
              )
            }
            className="font-mono-tab text-sm filter-yellow"
            data-testid="trend-m2-input"
          />
        </div>
      </div>

      {stockId && (
        <div className="flex flex-wrap items-center gap-3 -mt-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={savingConfig || savedList.length >= 3}
            onClick={async () => {
              setSavingConfig(true);
              try {
                await apiClient.post(`/stocks/${stockId}/saved-ma`, {
                  m1_days: m1DaysClamped,
                  m2_days: m2DaysClamped,
                  field,
                });
                toast.success("Configuração salva. Você será notificado a cada cruzamento.");
                await refreshSaved();
              } catch (e) {
                toast.error(e.response?.data?.detail || "Falha ao salvar configuração.");
              } finally {
                setSavingConfig(false);
              }
            }}
            data-testid="trend-save-config-button"
          >
            <Save className="w-3.5 h-3.5 mr-1.5" />
            {savedList.length >= 3
              ? "Limite de 3 atingido"
              : "Salvar essa configuração"}
          </Button>
          <span className="text-[11px] text-muted-foreground font-mono-tab">
            {savedList.length}/3 configurações salvas
          </span>
        </div>
      )}

      <div className="h-[420px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.92 0.004 286.32)" vertical={false} />
            <XAxis
              dataKey="raw_date"
              ticks={ticks}
              tickFormatter={monthYearLabel}
              interval={0}
              tick={{ fontSize: 10 }}
              stroke={GRAY}
            />
            <YAxis
              tick={{ fontSize: 10 }}
              stroke={GRAY}
              tickFormatter={(v) => (currency === "BRL" ? `R$ ${Number(v).toFixed(2)}` : `$${Number(v).toFixed(2)}`)}
              width={75}
              domain={yDomain}
              allowDataOverflow={false}
            />
            <Tooltip
              contentStyle={{
                background: "var(--popover)",
                border: "1px solid var(--border)",
                fontSize: 12,
              }}
              labelFormatter={fullDateLabel}
              formatter={(value, name) => {
                if (value == null) return ["—", name];
                return [Number(value).toFixed(4), name];
              }}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line
              type="monotone"
              dataKey="price"
              name={fieldLabel}
              stroke={GRAY}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              connectNulls
            />
            <Line
              type="monotone"
              dataKey="m1"
              name={`Média 1 · ${m1DaysClamped}d`}
              stroke={GREEN}
              strokeWidth={1.2}
              dot={false}
              isAnimationActive={false}
              connectNulls
            />
            <Line
              type="monotone"
              dataKey="m2_blue"
              name={`Média 2 · ${m2DaysClamped}d (≥ Média 1)`}
              stroke={M2_COLOR}
              strokeWidth={1.4}
              dot={false}
              isAnimationActive={false}
              connectNulls={false}
            />
            <Line
              type="monotone"
              dataKey="m2_red"
              name={`Média 2 · ${m2DaysClamped}d (< Média 1)`}
              stroke={M2_CROSS_COLOR}
              strokeWidth={1.6}
              strokeDasharray="6 4"
              dot={false}
              isAnimationActive={false}
              connectNulls={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
        Médias são móveis trailing — calculadas com a janela em dias informada.
        Quando o histórico não cobre a janela, a linha correspondente aparece vazia.
      </div>

      {interpretation && (
        <div
          className={`rounded-md border p-4 space-y-2 ${interpretationTone[interpretation.state] || ""}`}
          data-testid="trend-interpretation"
        >
          <div className="font-display text-base font-semibold">{interpretation.title}</div>
          <div className="text-sm leading-relaxed">{interpretation.text}</div>
          <div className="text-[10px] uppercase tracking-[0.2em] opacity-70 pt-2 border-t border-current/20">
            Análise apenas orientativa. A decisão final é do usuário, ponderando aspectos
            econômicos, cenário de mercado, riscos setoriais e sua situação de finanças pessoais.
          </div>
        </div>
      )}

      {stockId && (
        <div className="border-t border-border pt-4" data-testid="saved-ma-panel">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                Configurações salvas
              </div>
              <div className="text-sm text-muted-foreground">
                Cada configuração é avaliada diariamente e você recebe alerta a cada cruzamento (M1 × M2).
              </div>
            </div>
          </div>
          {loadingSaved ? (
            <div className="text-xs text-muted-foreground">Carregando…</div>
          ) : savedList.length === 0 ? (
            <div className="text-sm text-muted-foreground rounded-md border border-dashed border-border p-4 text-center">
              Nenhuma configuração salva. Ajuste as médias acima e clique em
              "Salvar essa configuração".
            </div>
          ) : (
            <ul className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="saved-ma-list">
              {savedList.map((cfg) => {
                const isBuy = cfg.current_signal === "buy";
                const isSell = cfg.current_signal === "sell";
                const toneClass = isBuy
                  ? "border-buy bg-buy-soft"
                  : isSell
                    ? "border-sell bg-sell-soft"
                    : "border-border";
                const Icon = isBuy ? TrendingUp : isSell ? TrendingDown : null;
                const signalLabel = isBuy
                  ? "Compra pelas suas médias"
                  : isSell
                    ? "Venda pelas suas médias"
                    : "Sem sinal (dados insuficientes)";
                const signalTone = isBuy ? "text-buy-strong" : isSell ? "text-sell-strong" : "text-muted-foreground";
                return (
                  <li
                    key={cfg.id}
                    className={`rounded-md border p-3 space-y-2 ${toneClass}`}
                    data-testid={`saved-ma-item-${cfg.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-xs font-display font-semibold">{cfg.label}</div>
                      <button
                        type="button"
                        onClick={async () => {
                          if (
                            !window.confirm(
                              `Excluir a configuração "${cfg.label}"?`,
                            )
                          )
                            return;
                          try {
                            await apiClient.delete(`/saved-ma/${cfg.id}`);
                            toast.success("Configuração excluída.");
                            await refreshSaved();
                          } catch {
                            toast.error("Falha ao excluir.");
                          }
                        }}
                        className="text-muted-foreground hover:text-sell-strong transition"
                        aria-label="Excluir configuração"
                        data-testid={`saved-ma-delete-${cfg.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className={`flex items-center gap-1.5 text-xs font-semibold ${signalTone}`}>
                      {Icon ? <Icon className="w-3.5 h-3.5" /> : null}
                      {signalLabel}
                    </div>
                    {cfg.m1_value != null && cfg.m2_value != null && (
                      <div className="text-[11px] text-muted-foreground font-mono-tab">
                        M1 {Number(cfg.m1_value).toFixed(2)} · M2 {Number(cfg.m2_value).toFixed(2)}
                      </div>
                    )}
                    {cfg.last_signal_date && (
                      <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-mono-tab">
                        Última avaliação: {cfg.last_signal_date}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
